import { ToastService } from 'root/shared/services/toast-service/toast.service';
import { inject, Injectable } from "@angular/core";
import { AuthService, DialogService, LoaderService, LogService } from 'root/services';
import { TermsAndConditionsTypes } from "../../legal/terms-and-conditions/terms-and-conditions.enum";
import { ToastType } from "root/shared/services/toast-service/toast.enum";
import { TermsAndConditionsService } from "root/platform/services";
import { DialogButtonType } from "root/shared/components/dialogs/dialog/dialog-button.enum";

@Injectable()
export class TermsAndConditionGateService {

  private termsAndConditionsService = inject(TermsAndConditionsService);
  private dialog = inject(DialogService);
  private authService = inject(AuthService);
  private loaderService = inject(LoaderService);
  private log = inject(LogService).withPrefix(this.constructor.name);
  private checkIteration = 0;
  private toast = inject(ToastService);

  public async assertTermsAndConditions(): Promise<boolean> {

    this.checkIteration++;

    this.log.debug(`Asserting terms and conditions, iteration ${this.checkIteration}`);

    const status = await this.termsAndConditionsService.getUserTermsAndConditionStatus();
    switch(status) {
      case 'accepted':
      case 'noNeedToAccept':
        this.log.debug(`Terms and conditions accepted, missing or no need to accept (${status})`);
        return true;
      case 'generalTermsMissing':
        this.log.error('General terms and conditions publish date is missing. This likely means that the environment is not properly configured. Showing toast to user.');
        this.toast.showTextWithTitle('General terms and conditions are currently unavailable. Please contact support.', 'Terms and conditions', { type: ToastType.Error });
        return true;
      case 'mustAcceptDataPrivacy':
        this.log.debug('User must accept data privacy statement');
        await this.termsAndConditionsService.showTermsAndConditions(TermsAndConditionsTypes.Privacy);
        break;
      case 'mustReacceptGeneralTerms':
        this.log.debug('User must re-accept general terms. Showing updated notice and general terms');
        await this.termsAndConditionsService.showTermsAndConditions(TermsAndConditionsTypes.Updated);
        break;
      case 'mustAcceptGeneralTerms':
        // Show general terms and conditions dialog
        this.log.debug('User must accept general terms');
        break;
    }

    // If we are here, it means user must accept terms and conditions
    await this.termsAndConditionsService.showTermsAndConditions(TermsAndConditionsTypes.General);

    do {
      // After showing the terms and conditions dialog, we attempt to accept the terms and conditions.
      // If it fails, we show a dialog to the user with options to retry or logout.
      // If the user chooses to retry, we will attempt to accept the terms and conditions again. If the user chooses to logout, we will sign the user out.
    } while (!await this.acceptTermsAndConditionsWithRetry());

    return true;
  }

  /**
   * Attempts to accept the terms and conditions, if it fails, shows a dialog to the user with options to retry or logout. If the user chooses to retry, it will attempt to accept the terms and conditions again. If the user chooses to logout, it will sign the user out.
   */
  private async acceptTermsAndConditionsWithRetry(): Promise<boolean> {
    try {
      await this.termsAndConditionsService.acceptTermsAndConditions();
      return true;
    } catch (error) {

      this.log.error(`Failed to accept terms and conditions`, error);

      const userReply = await this.dialog.promptWithButtons('Terms_And_Conditions_SaveError', [
        { id: 'logout', resourceKey: 'Global_Logout', type: DialogButtonType.Secondary },
        { id: 'retry', resourceKey: 'Global_Action_TryAgain', type: DialogButtonType.Primary }
      ]);

      if (userReply === 'retry') {
        return false;
      } else {
        this.loaderService.showFullScreenLoader();
        await this.authService.signout();
      }
    }
  }

}
